import { useCallback, useEffect, useRef, useState } from 'react'
import type { Notification as AppNotification } from '@/types'

/**
 * Fires native desktop notifications when new in-app notifications arrive.
 *
 * Foreground only (no service worker) — works while an Opsyri tab is open, which
 * covers the kiosk/always-open usage. Seeds the "seen" set on first load so it
 * never fires for the existing backlog, only for genuinely new unread items.
 */
export function useDesktopNotifications(notifications: AppNotification[] | undefined) {
  const seen = useRef<Set<string>>(new Set())
  const seeded = useRef(false)
  const supported = typeof window !== 'undefined' && 'Notification' in window
  const [permission, setPermission] = useState<NotificationPermission>(
    supported ? Notification.permission : 'denied',
  )

  const requestPermission = useCallback(async () => {
    if (!supported) return
    try {
      const p = await Notification.requestPermission()
      setPermission(p)
    } catch {
      /* Safari <16 uses a callback signature; ignore failures */
    }
  }, [supported])

  useEffect(() => {
    if (!notifications) return

    // First load: remember everything so we don't blast the backlog.
    if (!seeded.current) {
      notifications.forEach((n) => seen.current.add(n.id))
      seeded.current = true
      return
    }

    for (const n of notifications) {
      if (seen.current.has(n.id)) continue
      seen.current.add(n.id)
      if (n.read) continue
      if (permission !== 'granted' || !supported) continue
      try {
        const desktop = new Notification(n.title, { body: n.message ?? undefined, tag: n.id })
        desktop.onclick = () => {
          window.focus()
          desktop.close()
        }
      } catch {
        /* ignore */
      }
    }
  }, [notifications, permission, supported])

  return { permission, requestPermission, supported }
}
